<script>
import { mapActions, mapState } from 'vuex';
import SectionComponent from './sectionComponent.vue';

const mstl = 3000;

export default {
    props: {
        report_header: {
            type: Object,
            required: true
        },

        report_languages: {
            type: Object,
            required: true
        },

        selected_su2_survey_id: {
            type: Number,
            required: true
        }
    },

    components: {
        SectionComponent,
    },

    data() {
        return {
            created_sections: [],
            current_section_index: 0,

            seed_text: '',
            include_related_documents: false,
            selected_language: 'en',

            create_in_progress: false,
            close_popup_timeout: null,
        }
    },

    computed: {
        MAX_SEED_TEXT_LENGTH() {
            return mstl;
        },
        
        currentSeedText() {
            if (this.created_sections.length === 0)
                return this.seed_text;

            return this.created_sections[this.current_section_index].seed_text;
        },

        seedTextLengthValid() {
            return (this.currentSeedText.length <= this.MAX_SEED_TEXT_LENGTH);
        },

        seedTextLengthColor() {
            const ratio = this.currentSeedText.length / this.MAX_SEED_TEXT_LENGTH;
            
            if (ratio >= 0 && ratio < 0.7) {
                return "var(--sustainfinity-green)";
            } else if (ratio >= 0.7 && ratio < 0.95) {
                return "orange";
            } else if (ratio >= 0.95 && ratio < 1) {
                return "var(--invalid-input)";
            } else if (ratio >= 1) {
                return "red";
            } else {
                return "black";
            }
        },

        ...mapState('app', [
			'queued_jobs'
		]),
    },

    watch: {
        create_in_progress(newVal, oldVal) {
            if (!oldVal && newVal) {
                this.close_popup_timeout = setTimeout(() => {
                    this.$emit('close_popup');
                }, 30000);
            }
        }
    },

    beforeDestroy() {
        if (this.close_popup_timeout != null) {
            clearTimeout(this.close_popup_timeout);
        }
    },

    mounted() {
        if (!this.report_languages || this.report_languages.length === 0) {
            this.selected_language = 'en';
        } else {
            this.selected_language = this.report_languages[0];
        }

        this._getCreatedSections();
    },

    methods: {
        ...mapActions('reports', [
            'getCreatedSections',
            'createElementSection',
            'createSustSection',
        ]),

        _getCreatedSections() {
            if (!this.report_header) return;

            const params = {
                type: this.report_header.report_header_element_id != null ? 'element_section' : 'report_section',
                body: this.report_header,
            };

            this.getCreatedSections(params)
                .then(res => {
                    this.created_sections = res.data;
                })
        },

        async createSection() {
            if (this.create_in_progress || !this.seedTextLengthValid) return;

            const type = this.report_header.report_header_element_id != null ? 'element_section' : 'report_section';

            const jobId = crypto.randomUUID();
            const params = {
                company_id: this.$user.company_id,
                section_seed_text: this.currentSeedText,
                created_by: this.$user.user_id,
                evidence_document: this.include_related_documents,
                section_language: 'en',
                job_id: jobId
            };

            let api_call = null;
            if (type === 'element_section') {
                Object.assign(params, {sust_element_id: this.report_header.report_header_element_id});
                api_call = this.createElementSection;
            } else if (type === 'report_section') {
                Object.assign(params, {sust_report_section_name: this.report_header.report_header_abbreviation});
                Object.assign(params, {sust_report_section_order: this.report_header.report_header_order});
                Object.assign(params, {su2_survey_id: this.selected_su2_survey_id});
                api_call = this.createSustSection;
            } else return;

            if (this.selected_language === 'en' && this.report_languages.includes('tr')) {
                params.section_language = 'en-tr';
            } else if (this.selected_language === 'en' && !this.report_languages.includes('tr')) {
                params.section_language = 'en';
            } else if (this.selected_language === 'tr' && this.report_languages.includes('en')) {
                params.section_language = 'tr-en';
            } else if (this.selected_language === 'tr' && !this.report_languages.includes('en')) {
                params.section_language = 'tr';
            }

            this.$addQueuedJob({
                job_id: jobId, 
                options: {
                    path: '/generate-sections', 
                    query: { 'report-header-id': this.report_header.report_header_id },
                    type: 'section_generation'
                },
                result: null,
            });

            this.create_in_progress = true;
            await api_call(params)
                .then((res) => {
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
                .finally(() => {
                    this.create_in_progress = false;
                    /* const jobs = structuredClone(this.queued_jobs);
                    const idx = jobs.findIndex(x => x.job_id == jobId);
                    if (idx != -1) {
                        console.log("splicing the completed job");
                        jobs.splice(idx, 1);
						store.dispatch('app/setQueuedJobs', jobs);
                    } */
                })
        },
    }
}
</script>

<template>
    <div class="flex-all-centered-col">
        <div class="flex-all-centered-row report-header-name">
            <div>
                {{ report_header.report_header_name }}
            </div>
        </div>

        <div class="flex-all-centered-col">
            <div class="flex-all-centered-row" style="justify-content: flex-start; padding: .5em 0; color: var(--sustainfinity-navy);">
                {{ $t('myReports.generateSections.inputSeedText') }}
            </div>

            <textarea
                v-model="currentSeedText"
                rows="5"
                class="seed-text-area"
                :placeholder="$t('generateSections.placeholder_text')" 
                :maxlength="MAX_SEED_TEXT_LENGTH"
            ></textarea>

            <div class="flex-all-centered-row" style="justify-content: flex-start;" 
                :style="{
                    'color': seedTextLengthColor
                }"
            >
                {{ currentSeedText.length }} / {{ MAX_SEED_TEXT_LENGTH }}
            </div>
            
            <div class="flex-all-centered-row" style="justify-content: flex-end;">
                <div v-if="!create_in_progress" 
                    class="include-documents-wrapper"
                >
                    <label for="include-documents" style="margin: 0;">
                        {{ $t('generateSections.include_documents') }}
                    </label>
                    <input type="checkbox" id="include-documents" v-model="include_related_documents">
                </div>
                <div v-else
                    class="include-documents-wrapper"
                    style="color: var(--sustainfinity-blue); font-weight: bold;"
                >
                    {{ $t('section_generation.creating_you_continue') }}
                </div>

                <button @click="createSection()"
                    :disabled="create_in_progress || !seedTextLengthValid"
                    class="sust-btn sust-btn-blue generate-button"
                >
                    <loading-spinner v-if="create_in_progress" color="white" />
                    <span v-else>{{ $t("generateSections.buttons.generate") }}</span>
                </button>
            </div>

            <div class="flex-all-centered-row">
                <div class="flex-all-centered-row languages-wrapper">
                    <button v-for="language in report_languages" :key="language"
                        @click="() => selected_language = language"
                        :selected="selected_language === language"
                    >
                        {{ language }}
                    </button>
                </div>
            </div>

            <div v-if="created_sections.length > 1"
                class="flex-all-centered-row" style="justify-content: flex-start;"
            >
                <div>
                    {{ $t('generateSections.buttons.generated_text') }}: 
                </div>
                <div class="flex-all-centered-row" style="width: fit-content;">
                    <button class="icon-button" @click="$refs.sectionComponent.setCurrentSectionIndex(current_section_index-1, created_sections)">
                        <i class="fa-solid fa-caret-left"></i>
                    </button>

                    <div>
                        {{ current_section_index+1 }} / {{ created_sections.length }}
                    </div>

                    <button class="icon-button" @click="$refs.sectionComponent.setCurrentSectionIndex(current_section_index+1, created_sections)">
                        <i class="fa-solid fa-caret-right"></i>
                    </button>
                </div>
            </div>

            <div class="flex-all-centered-row" style="border-bottom: .15em solid var(--sustainfinity-blue); margin: .5em 0;"></div>

            <section-component v-if="created_sections.length > 0"
                ref="sectionComponent"
                :sections="created_sections"
                :report_header="report_header"
                :selected_language="selected_language"
                :languages="report_languages"
                :show_title="false"
                @delete="($event) => created_sections = $event"
                @changeIndex="($event) => current_section_index = $event"
            />
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .report-header-name {
        font-size: 1.5em;
        font-weight: bold;
        text-align: left;
        color: var(--sustainfinity-navy);
        justify-content: flex-start;

        > div {
            border-bottom: .15em solid var(--sustainfinity-navy);
        }
    }

    .seed-text-area {
        width: 100%;
        padding: .3em;
        border-radius: .25em;
        border: 0;
        outline: thin solid lightgray;

        &:focus {
            outline: thin solid black;
        }
    }

    .include-documents-wrapper {
        margin-right: 1em;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    
        >label {
            margin: 0 .25em 0 0 !important;
        }
    }

    .generate-button {
        transition: all 200ms;
        &:hover {
            background-color: var(--sustainfinity-faded-green);
            color: black;
            box-shadow: none;
            outline: thin solid lightgray !important;
        }
    }

    .languages-wrapper {
        outline: thin solid var(--sustainfinity-navy);
        width: fit-content !important;
        border-radius: .75em;
        overflow: hidden !important;

        > button {
            border: 0 !important;
            outline: 0 !important;
            padding: .75em;
            color: var(--sustainfinity-navy);
            background-color: whitesmoke;
            width: fit-content !important;
            text-transform: uppercase;

            &[selected] {
                color: whitesmoke !important;
                background-color: var(--sustainfinity-navy) !important;
                cursor: default !important;
            }

            &:hover {
                background-color: lightgray;
            }

            &:first-of-type {
                border-right: thin solid var(--sustainfinity-navy) !important;
            }
        }
    }
</style>